/** Leitura de cronogramas para importação: PDF impresso pelo MS Project, XML do MS Project e planilhas. */
import { parseDateTime, parseDurationHours, parsePredecessors, parseProjectPrint, type DepType, type ImportedSchedule, type ImportedTask, type TextItem } from '@siderproj/core';
import { mapRows, readSheet } from './importer';

export const ACCEPT = '.pdf,.xml,.xlsx,.csv';

export async function readScheduleFile(file: File): Promise<ImportedSchedule> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf') || file.type === 'application/pdf') return readPdf(file);
  if (name.endsWith('.xml')) return readXml(await file.text());
  if (name.endsWith('.xlsx') || name.endsWith('.csv')) return readTable(file);
  if (name.endsWith('.mpp')) throw new Error('O arquivo .mpp é fechado. No MS Project, use Arquivo > Salvar como > XML, ou imprima a tabela em PDF.');
  if (name.endsWith('.xer')) throw new Error('O formato .xer do Primavera ainda não é lido. Exporte a tabela de atividades para Excel (.xlsx).');
  throw new Error('Formato não suportado. Use PDF ou XML do MS Project, ou planilha .xlsx/.csv.');
}

/* PDF: textos e posições de cada página, lidos com o pdf.js */
async function readPdf(file: File): Promise<ImportedSchedule> {
  const pdfjs = await import('pdfjs-dist');
  const src = (await import('pdfjs-dist/build/pdf.worker.min.mjs?raw')).default;
  const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
  const worker = new Worker(url, { type: 'module' });
  try {
    pdfjs.GlobalWorkerOptions.workerPort = worker;
    const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
    const pages: { items: TextItem[] }[] = [];
    for (let i = 1; i <= Math.min(doc.numPages, 300); i++) {
      const tc = await (await doc.getPage(i)).getTextContent();
      pages.push({ items: tc.items.filter((it): it is typeof it & { str: string; transform: number[]; width: number } => 'str' in it).map(it => ({ str: it.str, x: it.transform[4]!, y: it.transform[5]!, w: it.width })) });
    }
    const out = parseProjectPrint(pages);
    if (!out.tasks.length && !pages.some(p => p.items.length)) throw new Error('Este PDF não tem texto (parece uma imagem escaneada). Imprima de novo pelo MS Project em PDF.');
    return out;
  } finally { worker.terminate(); URL.revokeObjectURL(url); }
}

/* XML do MS Project (Salvar como > XML) */
const XML_TYPE: Record<string, DepType> = { '0': 'FF', '1': 'FS', '2': 'SF', '3': 'SS' };
const isoDur = (s: string | null) => { const m = s?.match(/PT(\d+)H(\d+)M(\d+)S/); return m ? Number(m[1]) + Number(m[2]) / 60 : null; };

export function readXml(text: string): ImportedSchedule {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('O XML está corrompido ou incompleto.');
  const root = doc.documentElement;
  if (root.localName !== 'Project') throw new Error('Este XML não é um projeto do MS Project.');
  const kid = (el: Element, tag: string) => { for (const c of Array.from(el.children)) if (c.localName === tag) return c.textContent; return null; };
  const kids = (el: Element, tag: string) => Array.from(el.children).filter(c => c.localName === tag);
  const tasksEl = kids(root, 'Tasks')[0];
  const all = tasksEl ? kids(tasksEl, 'Task') : [];
  const warnings: string[] = [];
  const uidToId = new Map<string, number>();
  for (const t of all) uidToId.set(kid(t, 'UID') ?? '', Number(kid(t, 'ID') ?? 0));
  const minutesPerDay = Number(kid(root, 'MinutesPerDay') ?? 480) || 480;
  const tasks: ImportedTask[] = [];
  for (const t of all) {
    const id = Number(kid(t, 'ID') ?? 0); const level = Number(kid(t, 'OutlineLevel') ?? 1);
    if (level === 0 || id === 0 || kid(t, 'IsNull') === '1') continue; // tarefa-resumo do projeto
    const preds = kids(t, 'PredecessorLink').map(l => {
      const pid = uidToId.get(kid(l, 'PredecessorUID') ?? '');
      const lagMin = Number(kid(l, 'LinkLag') ?? 0) / 10; const fmt = kid(l, 'LagFormat');
      if (fmt && ['19', '20', '51', '52'].includes(fmt)) warnings.push(`Tarefa ${id}: atraso em porcentagem convertido para zero.`);
      return pid === undefined ? null : { id: pid, type: XML_TYPE[kid(l, 'Type') ?? '1'] ?? 'FS', lagHours: fmt && ['19', '20', '51', '52'].includes(fmt) ? 0 : (lagMin / 60) * (480 / minutesPerDay) };
    }).filter(Boolean) as ImportedTask['preds'];
    const as = kid(t, 'ActualStart'); const af = kid(t, 'ActualFinish');
    tasks.push({
      id, level, name: (kid(t, 'Name') ?? '').trim() || `Tarefa ${id}`, start: parseDateTime(kid(t, 'Start')), finish: parseDateTime(kid(t, 'Finish')),
      hours: isoDur(kid(t, 'Duration')), milestone: kid(t, 'Milestone') === '1', preds,
      percent: Number(kid(t, 'PercentComplete') ?? 0), actualStart: as ? as.slice(0, 10) : null, actualFinish: af ? af.slice(0, 10) : null, notes: kid(t, 'Notes')
    });
  }
  return { title: kid(root, 'Title') ?? kid(root, 'Name') ?? '', tasks, warnings, source: 'xml' };
}

/* Planilha exportada do MS Project ou do Primavera */
async function readTable(file: File): Promise<ImportedSchedule> {
  const rows = await readSheet(file);
  const { items, missing } = mapRows(rows, {
    id: ['Id', 'Nº', 'ID', 'Número'], name: ['Nome da tarefa', 'Nome', 'Task Name', 'Name', 'Activity Name', 'Atividade'],
    level: ['Nível da estrutura de tópicos', 'Nível', 'Outline Level'], outline: ['EDT', 'Número da estrutura de tópicos', 'WBS', 'Outline Number'],
    duration: ['Duração', 'Duration', 'Original Duration', 'Duração original'], start: ['Início', 'Start'], finish: ['Término', 'Finish'],
    preds: ['Predecessoras', 'Predecessors'], percent: ['% concluída', '% Complete', '% concluido']
  }, ['name']);
  if (missing.length) throw new Error(`A planilha precisa da coluna ${missing.join(', ')}. Colunas aceitas: Id, Nome, Nível ou EDT, Duração, Início, Término, Predecessoras.`);
  const warnings: string[] = []; const tasks: ImportedTask[] = [];
  items.forEach((r, i) => {
    const id = Number(r.id) || i + 1;
    const outline = String(r.outline ?? '').trim();
    const level = Number(r.level) || (outline ? outline.split('.').length : 1);
    const pr = parsePredecessors(r.preds as string);
    if (pr.invalid.length) warnings.push(`Linha ${i + 2}: predecessora não reconhecida (${pr.invalid.join(', ')}).`);
    const hours = typeof r.duration === 'number' ? r.duration * 8 : parseDurationHours(String(r.duration ?? ''));
    tasks.push({ id, level, name: String(r.name), start: parseDateTime(String(r.start ?? '')), finish: parseDateTime(String(r.finish ?? '')), hours, milestone: hours === 0, preds: pr.preds, percent: r.percent === '' ? null : Number(String(r.percent).replace('%', '').replace(',', '.')) || 0 });
  });
  return { title: file.name.replace(/\.[^.]+$/, ''), tasks, warnings, source: 'planilha' };
}
